import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Star, Compass } from "lucide-react";
import api from "@/lib/api";
import { WorldMap } from "@/components/WorldMap";

const STATUS = { wishlist: { label: "Want to go", color: "#D97706" }, visited: { label: "Visited", color: "#4D7C0F" } };

export default function SharedMap() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/shared/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This shared map is not available."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 font-mono text-sm text-muted-foreground">Loading shared map…</div>;
  if (error) return (
    <div className="mx-auto max-w-2xl px-4 py-20 text-center">
      <Compass className="mx-auto h-8 w-8 text-accent" />
      <h1 className="mt-4 font-serif text-2xl font-semibold text-foreground">Map unavailable</h1>
      <p className="mt-1 text-sm text-muted-foreground">{error}</p>
      <Link to="/" className="mt-5 inline-block bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest px-5 py-3">Go home →</Link>
    </div>
  );

  const favs = data.favorites || [];
  const points = favs.map((f) => ({ slug: f.slug, name: f.name, country: f.country, lat: f.lat, lng: f.lng, phase: f.phase, area: f.area, image: f.image, note: f.note, status: f.status }));

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-8">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-1.5 flex items-center gap-2">
        <Star className="h-3.5 w-3.5" /> Shared Collection
      </p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">{data.owner}'s Map of Mysteries</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">A read-only atlas of places {data.owner} has saved to explore.</p>

      {favs.length === 0 ? (
        <p className="mt-8 font-mono text-sm text-muted-foreground">This map has no places yet.</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <WorldMap points={points} height="70vh" mode="pins" colorBy="status" fitToPoints={true} />
            <div className="mt-2 flex items-center gap-4 font-mono text-[11px] uppercase tracking-widest text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {favs.length} saved</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS.wishlist.color }} /> Want to go</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS.visited.color }} /> Visited</span>
            </div>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Saved places</p>
            <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="shared-list">
              {favs.map((f) => {
                const st = STATUS[f.status] || STATUS.wishlist;
                return (
                  <li key={f.slug} className="border border-border bg-card p-3">
                    <div className="flex gap-3">
                      {f.image ? <img src={f.image} alt="" className="h-14 w-14 shrink-0 object-cover border border-border" /> : <span className="h-14 w-14 shrink-0 border border-border" style={{ background: `${st.color}22` }} />}
                      <div className="min-w-0">
                        <Link to={`/pyramid/${f.slug}`} className="font-serif text-base font-semibold text-foreground leading-tight hover:text-accent">{f.name}</Link>
                        <p className="font-mono text-[10px] uppercase tracking-wider mt-0.5" style={{ color: st.color }}>{st.label} · {f.country}</p>
                      </div>
                    </div>
                    {f.note && <p className="mt-2 text-sm text-foreground/90 border-l-2 pl-2.5 whitespace-pre-wrap" style={{ borderColor: st.color }}>{f.note}</p>}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
